/** The app's "Club" tab: news, notifications, the member's own messages, spiels. */
import { useQuery } from '@tanstack/react-query';
import { dateTime, shortDate, type Bonspiel, type Post } from '@lcc/shared';
import { api } from '../lib/api';
import { Card, Empty, ErrorNote, Loading, Pill, Status } from '../components/ui';
import { PageHeader } from '../App';

interface Notification { id: string; title: string; body: string | null; createdAt: string; readAt: string | null }
interface Thread {
  id: string; subject: string; state: string; updatedAt: string;
  lastMessage?: { body: string } | null;
}

export function ClubPage() {
  const posts = useQuery({
    queryKey: ['content', 'posts'],
    queryFn: () => api.get<{ data: Post[] }>('/content/posts?limit=20').then((r) => r.data),
  });
  const notes = useQuery({
    queryKey: ['notifications'],
    queryFn: () => api.get<{ unread: number; data: Notification[] }>('/comms/notifications'),
  });
  /*
   * `/messages` is the member's own threads with the office — not the office
   * inbox, which is `/messages/office/threads` and answers 403 here. The app
   * shows the same list.
   */
  const threads = useQuery({
    queryKey: ['messages', 'mine'],
    queryFn: () => api.get<{ data: Thread[]; unread: number }>('/messages'),
  });
  const spiels = useQuery({
    queryKey: ['bonspiels'],
    queryFn: () => api.get<{ data: Bonspiel[] }>('/bonspiels').then((r) => r.data),
  });

  const upcoming = (spiels.data ?? [])
    .filter((s) => s.startsOn >= new Date().toISOString().slice(0, 10))
    .sort((a, b) => a.startsOn.localeCompare(b.startsOn));

  return (
    <>
      <PageHeader
        title="The club"
        sub="News, your messages with the office, and what is coming up"
      />
      <div className="content stack">
        {notes.data && notes.data.data.length > 0 && (
          <Card title={notes.data.unread ? `Notifications (${notes.data.unread} unread)` : 'Notifications'}>
            {notes.data.data.slice(0, 6).map((n) => (
              <div key={n.id} className="row spread" style={{ padding: '.5rem 0' }}>
                <span>
                  <b>{n.title}</b>
                  {n.body && <span className="small muted"> — {n.body}</span>}
                </span>
                <span className="small muted nowrap">{dateTime(n.createdAt)}</span>
              </div>
            ))}
          </Card>
        )}

        <Card title="Your messages with the office">
          {threads.isLoading && <Loading />}
          {threads.error && <ErrorNote error={threads.error} />}
          {threads.data && threads.data.data.length === 0 && (
            <Empty title="No conversations yet">
              Anything you send the office appears here, with their replies.
            </Empty>
          )}
          {threads.data && threads.data.data.length > 0 && (
            <div className="table-wrap">
              <table className="t">
                <caption className="sr-only">Your message threads</caption>
                <thead><tr><th>Subject</th><th>Last</th><th>State</th></tr></thead>
                <tbody>
                  {threads.data.data.map((t) => (
                    <tr key={t.id}>
                      <td>
                        {t.subject}
                        {t.lastMessage?.body && (
                          <span className="small muted"> — {t.lastMessage.body.slice(0, 80)}</span>
                        )}
                      </td>
                      <td className="nowrap small muted">{dateTime(t.updatedAt)}</td>
                      <td><Status value={t.state} /></td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </Card>

        <Card title="News">
          {posts.isLoading && <Loading />}
          {posts.error && <ErrorNote error={posts.error} />}
          {posts.data && posts.data.length === 0 && <Empty title="No news posted" />}
          {posts.data?.map((p) => (
            <article key={p.id} style={{ paddingBottom: '1rem' }}>
              <p className="small muted" style={{ marginBottom: '.2rem' }}>
                {p.publishedAt ? shortDate(p.publishedAt.slice(0, 10)) : 'Draft'}
                {p.isPinned && <> · <Pill tone="warn">Pinned</Pill></>}
              </p>
              <h3 style={{ margin: '0 0 .3rem' }}>{p.title}</h3>
              {p.excerpt && <p style={{ margin: 0 }}>{p.excerpt}</p>}
            </article>
          ))}
        </Card>

        <Card title="Bonspiels">
          {spiels.isLoading && <Loading />}
          {upcoming.length === 0 && !spiels.isLoading && (
            <Empty title="Nothing scheduled yet">
              Spiel dates are confirmed through the season.
            </Empty>
          )}
          {upcoming.length > 0 && (
            <div className="table-wrap">
              <table className="t">
                <caption className="sr-only">Upcoming bonspiels</caption>
                <thead><tr><th>Spiel</th><th>When</th><th>Kind</th></tr></thead>
                <tbody>
                  {upcoming.map((b) => (
                    <tr key={b.id}>
                      <td>{b.name}</td>
                      <td className="nowrap">
                        {shortDate(b.startsOn)}
                        {b.endsOn !== b.startsOn && <> – {shortDate(b.endsOn)}</>}
                      </td>
                      <td><Pill tone="quiet">{b.kind.replace(/_/g, ' ')}</Pill></td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </Card>
      </div>
    </>
  );
}
